import { type ChangeEvent, type FC } from "react";
import { type LinearCstic, type LinearCsticValue, type LinearSubstep } from "../../Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "../../Linear/components/SubstepFrame";
import { getCsticMap, selectSchmieranschlussTabConfig } from "../../Linear/redux/selectors";
import {
  CARRIAGE_DESIGN_SUBKMAT_KWVE,
  CARRIAGE_DESIGN_SUBKMAT_RWU,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_DESIGN_TYPE_SUBLEVEL_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_SUPPORT_KIT_NAME,
  KITS_LOWER_THRESHOLD,
  KITS_UPPER_THRESHOLD,
  MOUNTED_LEFT,
  MOUNTED_RIGHT,
  UNMOUNTED_LEFT,
  UNMOUNTED_RIGHT,
} from "../../Linear/constants";
import { LinearSection, LinearSectionTitle } from "../../Linear/components/Substeps/GuidewaySetStep";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "../../../redux/types";
import { type RootState } from "../../../redux/store";
import { editCarriageDetails, updateCsticValues } from "../../Linear/redux/actions";
import { SubstepType } from "../../Linear/dtos";
import { Col, Row } from "../../../common/components/Layout";
import PopupSelectionCstic from "../../Linear/components/SingleCstics/PopupSelectionCstic";
import LanguageAwareExternalContent from "../../Linear/components/LanguageAwareExternalContent";
import { Card } from "../../Linear/components/Card";
import { CARRIAGE_DETAILS_INFO_MSG } from "../carriageConfiguratorConstants";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../common/components/GridGappedContainer/GridGappedContainer";
import { Button } from "../../../common/components/Button/Button";
import { resetCarriageDetails } from "../redux/actions";
import {
  isSchmierAuswahlCsticVisible,
  isSchmierDegreeCsticVisible,
  isSchmierPositionCsticVisible,
} from "../carriageConfiguratorUtils";
import { LinearTranslation } from "../../common/components/LinearTranslation";
import { RESET_STANDARD } from "../../common/advancedTranslationUtils";
import { SimpleSelect } from "../../../common/components/Select/SimpleSelect";
import { KitInfoWrapper } from "../../Linear/components/Substeps/CarriageDetailsSubstep";
import { SimpleSelectWithStar } from "~/common/components/Select/SimpleSelectWithStar";
import { type DropdownEntry } from "~/common/types";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import { Alert, type SelectChangeEvent } from "@mui/material";
import * as CarriageCstic from "../v2/configuration/CarriageCstics";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const CarriageDetailsSubstep: FC<Props> = ({ def, ...props }) => {
  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} loadedClassname={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}

      <div className={"w-100 mb-30"}>
        <Row className={"mt-30"}>
          <Col size={4}>
            <SingleTile carriageIndex={0} index={0} guidewayIndex={0} guidewayAmount={1} carriageAmount={1} def={def} />
          </Col>
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default CarriageDetailsSubstep;

interface SingleGuidewayTabProps {
  guidewayIndex: number;
  startingCarriageIndex: number;
  carriageAmount: number;
  guidewayAmount: number;
}

interface TileProps {
  carriageIndex: number; // Carriage index in redux - 0 based
  guidewayIndex: number; // Guideway index in redux - 0 based
  index: number; // Index of carriage in guideway - 0 based
  guidewayAmount: number;
  carriageAmount: number;
  def: LinearSubstep;
}

const SingleTile: FC<TileProps> = ({
  def,
  carriageIndex,
  index,
  guidewayIndex,
  guidewayAmount,
  carriageAmount,
}: TileProps) => {
  const dispatch = useDispatch();
  const cstics = useSelector(getCsticMap(KmatType.CARRIAGE, carriageIndex));
  const tabConfig = useSelector(selectSchmieranschlussTabConfig);

  const leftCstic = cstics[CSTIC_CARRIAGE_LEFT_NAME];
  const leftCsticDDValues = makeDropdownElements(leftCstic, carriageIndex, dispatch, true);
  const leftCsticSelectedEl = leftCsticDDValues.filter((el) => el.value === leftCstic.value)[0];

  const middleCstic = cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
  const middleCsticDDValues = makeDropdownElements(middleCstic, carriageIndex, dispatch, true);
  const middleCsticSelectedEl = middleCsticDDValues.filter((el) => el.value === middleCstic.value)[0];

  const rightCstic = cstics[CSTIC_CARRIAGE_RIGHT_NAME];
  const rightCsticDDValues = makeDropdownElements(rightCstic, carriageIndex, dispatch, true);
  const rightCsticSelectedEl = rightCsticDDValues.filter((el) => el.value === rightCstic.value)[0];

  const schmierCstic = cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
  const designTypeCstic = cstics[CSTIC_DESIGN_TYPE_SUBLEVEL_NAME];
  let leftKitsNumericValue = 0;
  let rightKitsNumericValue = 0;
  if (leftCstic && leftCstic.value) {
    leftKitsNumericValue = isNaN(Number(leftCstic.value)) ? 0 : Number(leftCstic.value);
  }
  if (rightCstic && rightCstic.value) {
    rightKitsNumericValue = isNaN(Number(rightCstic.value)) ? 0 : Number(rightCstic.value);
  }
  const schmierCsticDDValues = makeDropdownElementsForLubricationAdapter(
    schmierCstic,
    designTypeCstic,
    leftKitsNumericValue,
    rightKitsNumericValue,
    carriageIndex,
    dispatch,
  );
  const schmierCsticSelectedEl = schmierCsticDDValues.filter((el) => el.value === schmierCstic.value)[0];

  const schmierDegreeCstic = cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];

  const schmierDegreeCsticDDValues = makeDropdownElements(schmierDegreeCstic, carriageIndex, dispatch, false);
  const schmierDegreeCsticSelectedEl = schmierDegreeCsticDDValues.filter(
    (el) => el.value === schmierDegreeCstic.value,
  )[0];

  const lubricationConnectionOrientationCstic = cstics[CarriageCstic.LubricationConnectorOrientation];
  const lubricationConnectionOrientationCsticDDValues = makeDropdownElements(
    lubricationConnectionOrientationCstic,
    carriageIndex,
    dispatch,
    false,
  );
  const lubricationConnectionOrientationCsticSelectedEl = lubricationConnectionOrientationCsticDDValues.filter(
    (el) => el.value === lubricationConnectionOrientationCstic.value,
  )[0];

  const lubricationConnectorCstic = cstics[CarriageCstic.LubricationConnector];

  const kitSupportCstic = cstics[CSTIC_SUPPORT_KIT_NAME];

  const isLeftsKitsValid = leftCsticSelectedEl && leftCsticSelectedEl.value != undefined;
  const isMiddleKitsValid = middleCsticSelectedEl && middleCsticSelectedEl.value != undefined;
  const isRightKitsValid = rightCsticSelectedEl && rightCsticSelectedEl.value != undefined;
  const isLubricationConnectorValid = lubricationConnectorCstic && lubricationConnectorCstic.value != undefined;
  const islubricationConnectionOrientationValid =
    lubricationConnectionOrientationCstic && lubricationConnectionOrientationCstic.value != undefined;
  const isSchmierDegreeValid = schmierDegreeCstic && schmierDegreeCstic.value != undefined;

  const isRightVisible = rightCstic && isLeftsKitsValid;
  const isMiddleVisible = middleCstic && isRightKitsValid;

  const kitsConfigured = isLeftsKitsValid && isMiddleKitsValid && isRightKitsValid;

  const schmierCsticVisible = kitsConfigured;

  const schmierDegreeCsticVisible = isSchmierDegreeCsticVisible(cstics);
  const lubricationConnectionOrientationCsticVisible = isSchmierPositionCsticVisible(cstics);
  const schmierAuswahlCsticVisible = isSchmierAuswahlCsticVisible(cstics);

  const upperDisclaimer = useSelector(selectUpperDisclaimer);
  const enableConfigureCarriageValues = useSelector(shouldEnableConfigureCarriageValues);

  return (
    <Card disableHover={true} className={"pt-3"}>
      <div className={"px-gutter-half"}>
        {upperDisclaimer && (
          <Alert className="mb-30" severity="info">
            <LanguageAwareExternalContent content={upperDisclaimer} loadedClassname={"scha__lc__external-label"} />
          </Alert>
        )}

        <LinearSectionTitle className={"mt-0 mb-3 "} info={def.extraTexts["sealingAndLubricationSectionInfo"]}>
          <LanguageAwareExternalContent
            content={def.extraTexts["sealingAndLubricationSectionName"]}
            loadedClassname={"scha__lc__external-label"}
          />
        </LinearSectionTitle>

        <LinearSection label={""} info={kitSupportCstic.info}>
          <div className={"scha__lc__kit"}>
            {leftCstic && (
              <KitInfoWrapper cstic={leftCstic} addModalParam={true}>
                <SimpleSelectWithStar
                  label={
                    <LanguageAwareExternalContent
                      content={leftCstic.label}
                      loadedClassname={"scha__lc__external-label"}
                    />
                  }
                  values={leftCsticDDValues}
                  shrink={true}
                  value={leftCsticSelectedEl ? leftCsticSelectedEl.value : null}
                  onChange={DROPDOWN_ON_CLICK2(leftCsticDDValues)}
                  showFilter={true}
                  error={!isLeftsKitsValid}
                  fullWidth={true}
                  disabled={!enableConfigureCarriageValues}
                />
              </KitInfoWrapper>
            )}

            {isMiddleVisible && (
              <KitInfoWrapper cstic={middleCstic} addModalParam={true}>
                <SimpleSelectWithStar
                  label={
                    <LanguageAwareExternalContent
                      content={middleCstic.label}
                      loadedClassname={"scha__lc__external-label"}
                    />
                  }
                  values={middleCsticDDValues}
                  shrink={true}
                  value={middleCsticSelectedEl ? middleCsticSelectedEl.value : null}
                  onChange={DROPDOWN_ON_CLICK2(middleCsticDDValues)}
                  showFilter={true}
                  error={!isMiddleKitsValid}
                  fullWidth={true}
                  disabled={!enableConfigureCarriageValues}
                />
              </KitInfoWrapper>
            )}

            {isRightVisible && (
              <KitInfoWrapper cstic={rightCstic} addModalParam={true}>
                <SimpleSelectWithStar
                  label={
                    <LanguageAwareExternalContent
                      content={rightCstic.label}
                      loadedClassname={"scha__lc__external-label"}
                    />
                  }
                  values={rightCsticDDValues}
                  shrink={true}
                  value={rightCsticSelectedEl ? rightCsticSelectedEl.value : null}
                  onChange={DROPDOWN_ON_CLICK2(rightCsticDDValues)}
                  showFilter={true}
                  error={!isRightKitsValid}
                  fullWidth={true}
                  disabled={!enableConfigureCarriageValues}
                />
              </KitInfoWrapper>
            )}
          </div>
        </LinearSection>

        {schmierCsticVisible && schmierCstic && (
          <LinearSection
            info={def.extraTexts["lubricationConnectorSectionInfo"]}
            label={
              <LanguageAwareExternalContent
                content={def.extraTexts["lubricationConnectorSectionName"]}
                loadedClassname={"scha__lc__external-label"}
              />
            }
          >
            <SimpleSelectWithStar
              values={schmierCsticDDValues}
              value={schmierCsticSelectedEl ? schmierCsticSelectedEl.value : null}
              onChange={DROPDOWN_ON_CLICK2(schmierCsticDDValues)}
              disabled={!enableConfigureCarriageValues}
              fullWidth={true}
            />
          </LinearSection>
        )}

        {lubricationConnectionOrientationCsticVisible && lubricationConnectionOrientationCstic && (
          <LinearSection
            info={lubricationConnectionOrientationCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationConnectionOrientationCstic.label}
                loadedClassname={"scha__lc__external-label"}
              />
            }
          >
            <SimpleSelect
              values={lubricationConnectionOrientationCsticDDValues}
              value={
                lubricationConnectionOrientationCsticSelectedEl
                  ? lubricationConnectionOrientationCsticSelectedEl.value
                  : undefined
              }
              onChange={DROPDOWN_ON_CLICK(lubricationConnectionOrientationCsticDDValues)}
              error={!islubricationConnectionOrientationValid}
              fullWidth={true}
              disabled={!enableConfigureCarriageValues}
            />
          </LinearSection>
        )}

        {schmierAuswahlCsticVisible && lubricationConnectorCstic && lubricationConnectorCstic.values.length > 0 && (
          <PopupSelectionCstic
            className={"mb-30"}
            carriageIndex={carriageIndex}
            cstic={cstics[CarriageCstic.LubricationConnector]}
            substep={SubstepType.ConfigureCarriage}
            tabConfig={tabConfig}
            hasError={!isLubricationConnectorValid}
            disabled={!enableConfigureCarriageValues}
          />
        )}

        {schmierDegreeCsticVisible && schmierDegreeCstic && (
          <>
            <LinearSection
              info={schmierDegreeCstic.info}
              label={
                <LanguageAwareExternalContent
                  content={schmierDegreeCstic.label}
                  loadedClassname={"scha__lc__external-label"}
                />
              }
            >
              <SimpleSelect
                values={schmierDegreeCsticDDValues}
                value={schmierDegreeCsticSelectedEl ? schmierDegreeCsticSelectedEl.value : undefined}
                onChange={DROPDOWN_ON_CLICK(schmierDegreeCsticDDValues)}
                error={!isSchmierDegreeValid}
                fullWidth={true}
                disabled={!enableConfigureCarriageValues}
              />
            </LinearSection>
          </>
        )}

        <GridGappedContainer gapSize={GridGappedContainerSize.S_16} className={"pb-3"}>
          <Button color={"secondary"} fullWidth={true} onClick={() => dispatch(resetCarriageDetails())}>
            <LinearTranslation translationKey={RESET_STANDARD} />
          </Button>
          <Button
            color={"primary"}
            fullWidth={true}
            onClick={() => dispatch(editCarriageDetails(carriageIndex, SubstepType.ConfigureCarriage))}
          >
            <LanguageAwareExternalContent
              content={def.extraTexts["edit"]}
              loadedClassname={"scha__lc__external-label"}
            />
          </Button>
        </GridGappedContainer>
      </div>
    </Card>
  );
};

type EnhancedDropdownEntry = DropdownEntry<string> & { onClick: () => void };

const makeDropdownElements = defaultMemoize(
  (
    cstic: LinearCstic | undefined,
    kmatIndex: number,
    dispatch: CustomDispatch<RootState>,
    isFilterable: boolean,
  ): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    return cstic.values.map((csticVal) => ({
      label: <LanguageAwareExternalContent content={csticVal.label} loadedClassname={"scha__lc__external-label"} />,
      value: csticVal.key,
      stringLabel: isFilterable ? csticVal.label : undefined,
      onClick: () =>
        dispatch(
          updateCsticValues(
            [
              {
                type: KmatType.CARRIAGE,
                value: csticVal.value,
                key: cstic.key,
                index: kmatIndex,
              },
            ],
            SubstepType.ConfigureCarriage,
          ),
        ),
      defaultValue: csticVal.standard,
    }));
  },
);

const makeDropdownElementsForLubricationAdapter = defaultMemoize(
  (
    cstic: LinearCstic | undefined,
    designTypeCstic: LinearCstic | undefined,
    leftKitsNumericValue: number,
    rightKitsNumericValue: number,
    kmatIndex: number,
    dispatch: CustomDispatch<RootState>,
  ): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    if (!cstic.values) {
      return [];
    }

    return cstic.values
      .filter((csticVal) => {
        return isValueAllowed(csticVal, designTypeCstic, leftKitsNumericValue, rightKitsNumericValue);
      })
      .map((csticVal) => ({
        label: <LanguageAwareExternalContent content={csticVal.label} loadedClassname={"scha__lc__external-label"} />,
        value: csticVal.key,
        onClick: () =>
          dispatch(
            isSettingOS(csticVal)
              ? noLubricationUpdate(cstic, csticVal, kmatIndex)
              : regularUpdate(cstic, csticVal, kmatIndex),
          ),
        defaultValue: csticVal.standard,
      }));
  },
);

const DROPDOWN_ON_CLICK = (allValues: EnhancedDropdownEntry[]) => (e: ChangeEvent<HTMLInputElement>) => {
  const val = e.target.value as string;
  if (val) {
    allValues.find((item) => item.value === val)?.onClick();
  }
};
const DROPDOWN_ON_CLICK2 = (allValues: EnhancedDropdownEntry[]) => (e: SelectChangeEvent<unknown>) => {
  const val = e.target.value as string;
  if (val) {
    allValues.find((item) => item.value === val)?.onClick();
  }
};

const isValueAllowed = (
  csticValue: LinearCsticValue | null | undefined,
  designTypeCstic: LinearCstic | undefined,
  leftKitsNumericValue: number,
  rightKitsNumericValue: number,
) => {
  if (!csticValue || !csticValue.value) {
    return false;
  }

  if (designTypeCstic) {
    if (designTypeCstic.value === CARRIAGE_DESIGN_SUBKMAT_RWU) {
      if (leftKitsNumericValue >= KITS_LOWER_THRESHOLD && leftKitsNumericValue <= KITS_UPPER_THRESHOLD) {
        return csticValue.value !== MOUNTED_LEFT && csticValue.value !== UNMOUNTED_LEFT;
      }
      if (rightKitsNumericValue >= KITS_LOWER_THRESHOLD && rightKitsNumericValue <= KITS_UPPER_THRESHOLD) {
        return csticValue.value !== MOUNTED_RIGHT && csticValue.value !== UNMOUNTED_RIGHT;
      }
    } else if (designTypeCstic.name === CARRIAGE_DESIGN_SUBKMAT_KWVE) {
      if (leftKitsNumericValue == KITS_LOWER_THRESHOLD) {
        return csticValue.value !== MOUNTED_LEFT && csticValue.value !== UNMOUNTED_LEFT;
      }
      if (rightKitsNumericValue == KITS_LOWER_THRESHOLD) {
        return csticValue.value !== MOUNTED_RIGHT && csticValue.value !== UNMOUNTED_RIGHT;
      }
    }
  }

  return true;
};

const isSettingOS = (csticVal: LinearCsticValue) => csticVal.value === "OS";

const regularUpdate = (cstic: LinearCstic, csticVal: LinearCsticValue, kmatIndex: number) =>
  updateCsticValues(
    [
      {
        type: KmatType.CARRIAGE,
        value: csticVal.value,
        key: cstic.key,
        index: kmatIndex,
      },
    ],
    SubstepType.ConfigureCarriage,
  );

const noLubricationUpdate = (cstic: LinearCstic, csticVal: LinearCsticValue, kmatIndex: number) =>
  updateCsticValues(
    [
      {
        type: KmatType.CARRIAGE,
        value: csticVal.value,
        key: cstic.key,
        index: kmatIndex,
      },
      {
        type: KmatType.CARRIAGE,
        value: "",
        key: CarriageCstic.LubricationConnector,
        index: kmatIndex,
      },
      {
        type: KmatType.CARRIAGE,
        value: "",
        key: CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
        index: kmatIndex,
      },
      {
        type: KmatType.CARRIAGE,
        value: "",
        key: CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
        index: kmatIndex,
      },
    ],
    SubstepType.ConfigureCarriage,
  );

const selectUpperDisclaimer = (state: RootState) =>
  state.linearConfigurator.specialTranslations[CARRIAGE_DETAILS_INFO_MSG];
const shouldEnableConfigureCarriageValues = (state: RootState) =>
  state.linearConfigurator.enableConfigureCarriageValues;
